import { computed, ref } from 'vue';

import {
  ApiError,
  api,
  onSessionRefreshed,
  refreshSession,
  type PublicPlayer,
} from '@/net/api';

/**
 * Who is signed in.
 *
 * A single module-scoped store rather than a provide/inject tree: there is
 * exactly one session per tab, and passing it down through three screens would
 * be ceremony around a global that is genuinely global.
 */
/**
 * A hint, not a credential.
 *
 * The refresh cookie is httpOnly, so the page cannot tell whether it has one.
 * Without this flag every cold visit fires a `POST /api/auth/refresh` that a
 * first-time visitor is guaranteed to get a 401 from — a wasted round trip and
 * a red line in their console. The flag says only "this browser signed in at
 * some point"; it grants nothing, and the cookie remains the sole authority.
 */
const SESSION_HINT = 'admiralty.session';

const player = ref<PublicPlayer | null>(null);
const restoring = ref(true);
const busy = ref(false);
const error = ref<string | null>(null);

onSessionRefreshed((session) => {
  player.value = session.player;
  localStorage.setItem(SESSION_HINT, '1');
});

export function useSession() {
  return {
    player: computed(() => player.value),
    signedIn: computed(() => player.value !== null),
    restoring: computed(() => restoring.value),
    busy: computed(() => busy.value),
    error: computed(() => error.value),
    register,
    login,
    logout,
    restore,
    sync,
    clearError,
    setBalances,
    setNickname,
  };
}

/**
 * Re-reads who is signed in, from the server, right now.
 *
 * **Why this is not `refreshSession()`.** That function deduplicates concurrent
 * callers through a module-level `refreshing` promise, which is correct for its
 * job — several 401s must not race each other — and wrong for this one: after
 * redeeming a verification token the caller needs an answer that reflects the
 * redemption, and the dedupe will happily hand back an in-flight refresh that
 * *started before it*. That is exactly what happened. `App.vue` fires
 * `restore()` on mount, `VerifyEmailScreen` redeems the token and then asked for
 * a refresh, got the mount's promise back carrying `emailVerified: false`, and
 * the app dropped onto the verification gate it had just been let through.
 *
 * `GET /api/auth/me` has no such sharing, and `JwtStrategy.validate` reads the
 * column from the database on every request — so this is always the current
 * answer, with no new token needed.
 *
 * Failure is a `false` and no state change: a link opened in a browser with no
 * session is a real case, and the right outcome there is "sign in", not an error.
 */
async function sync(): Promise<boolean> {
  try {
    const { player: current } = await api.me();
    player.value = current;
    localStorage.setItem(SESSION_HINT, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Called once at start-up. The access token died with the last page, but the
 * refresh cookie did not, so a signed-in player lands straight in the game.
 */
async function restore(): Promise<void> {
  restoring.value = true;
  try {
    if (localStorage.getItem(SESSION_HINT) !== '1') return;
    // The cookie can still have expired or been revoked since.
    if (!(await refreshSession())) localStorage.removeItem(SESSION_HINT);
  } finally {
    restoring.value = false;
  }
}

/**
 * `altcha` is the solved proof-of-work payload from the widget on the join
 * screen — the template's auth requires one on both calls. The call sign is
 * posted as the template's `username`.
 */
async function register(input: {
  nickname: string;
  email: string;
  password: string;
  altcha: string;
}, beforeHandoff?: () => Promise<void>): Promise<boolean> {
  return run(async () => {
    const registered = await api.register({
      username: input.nickname,
      email: input.email,
      password: input.password,
      altcha: input.altcha,
    });
    // The join screen only submits with the terms ticked. Core's register cannot
    // take the flag, so the server records it now that the cookies are set.
    // TODO(arturs): a drop between the two requests leaves no acceptance row (TD-LL-71)
    await recordTermsAcceptance();
    return registered;
  }, beforeHandoff);
}

/**
 * Never blocks the sign-up: the account exists either way, and a failed record
 * is logged rather than shown to a player who did nothing wrong.
 */
async function recordTermsAcceptance(): Promise<void> {
  try {
    await api.acceptTerms();
  } catch (error) {
    console.warn('[admiralty] terms acceptance not recorded', error);
  }
}

async function login(input: {
  email: string;
  password: string;
  altcha: string;
}, beforeHandoff?: () => Promise<void>): Promise<boolean> {
  return run(() => api.login(input), beforeHandoff);
}

async function logout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Signing out locally matters more than the server acknowledging it.
  }
  player.value = null;
  localStorage.removeItem(SESSION_HINT);
}

/**
 * Keeps the header balances in step after a purchase, without a second fetch.
 * The template's auth answers with zero balances; whichever game screen
 * fetches the wallet calls this.
 */
function setBalances(credits: number, diamonds: number): void {
  if (player.value) player.value = { ...player.value, credits, diamonds };
}

/**
 * Keeps the header (and everything else reading `useSession().player`) in
 * step after a call-sign change, on the same reasoning as `setBalances`.
 *
 * `state/account.ts`'s `profile` ref is a *second* copy of the player's own
 * name, and `saveCallSign` updates that one from the server's response. Without
 * this the header kept the name this session started with until the access
 * token's next silent refresh, up to 15 minutes later.
 */
function setNickname(nickname: string): void {
  if (player.value) player.value = { ...player.value, nickname };
}

function clearError(): void {
  error.value = null;
}

/**
 * `beforeHandoff` runs after the server has said yes and before the player is
 * set — setting it is what makes the app swap the enrolment form for the
 * harbour, so this is the moment the form gets to stamp ENLISTED on itself
 * (life-layer.md brief, item 3). The form stays busy while it runs.
 */
async function run(action: () => Promise<{ player: PublicPlayer }>, beforeHandoff?: () => Promise<void>) {
  busy.value = true;
  error.value = null;
  try {
    const session = await action();
    await beforeHandoff?.();
    player.value = session.player;
    localStorage.setItem(SESSION_HINT, '1');
    return true;
  } catch (err) {
    error.value =
      err instanceof ApiError ? err.message : 'Could not reach the server. Is it running?';
    return false;
  } finally {
    busy.value = false;
  }
}
